import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { SummaryCard } from "../components/home/SummaryCard";
import { useControlCash } from "../store/controlCashStore";
import { useConfirm } from "../contexts/ConfirmContext";

// ============================================================================
// HELPERS
// ============================================================================
const clampPercent = (current, target) => {
  if (target === 0) return 0;
  const percent = (current / target) * 100;
  return Math.min(Math.max(percent, 0), 100);
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-PE", { 
    day: "2-digit", 
    month: "short",
    year: "numeric"
  });
};

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// ============================================================================
// COMPONENTE: PageHeader
// ============================================================================
const PageHeader = ({ onBack }) => (
  <div className="mb-8">
    <button
      onClick={onBack}
      className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mb-4 transition-colors group"
      aria-label="Volver al inicio"
    >
      <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      Volver
    </button>
    <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
      Metas de ahorro
    </h1>
    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
      Crea metas, registra aportes y sigue tu progreso.
    </p>
  </div>
);

// ============================================================================
// COMPONENTE: SummaryStats (diseño unificado con Home)
// ============================================================================
const SummaryStats = ({ goals, contributions, formatAmount, currency }) => {
  const activeGoals = goals.filter(g => g.status === "ACTIVE");
  const completedGoals = goals.filter(g => g.status === "COMPLETED");
  const totalSaved = contributions.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
      <SummaryCard title="Metas activas" amount={String(activeGoals.length)} currency={currency} color="blue" />
      <SummaryCard title="Metas completadas" amount={String(completedGoals.length)} currency={currency} color="green" />
      <SummaryCard title="Total ahorrado" amount={formatAmount(totalSaved)} currency={currency} color="orange" />
    </div>
  );
};

// ============================================================================
// COMPONENTE: GoalCard
// ============================================================================
const GoalCard = ({ 
  goal, 
  currentAmount, 
  contributions,
  onAddContribution, 
  onEdit, 
  onDelete,
  onDeleteContribution,
  formatAmount
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showContributions, setShowContributions] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = () => setIsMobileView(!mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const showCollapsed = !isExpanded && isMobileView;
  const showExpanded = isExpanded || !isMobileView;
  const progress = clampPercent(currentAmount, goal.targetAmount);
  const isCompleted = goal.status === "COMPLETED";
  const goalContributions = contributions.filter(c => c.goalId === goal.id);

  return (
    <div className={`rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden bg-white dark:bg-gray-800 border ${
      isCompleted ? "border-2 border-green-200 dark:border-green-700/60" : "border-gray-100 dark:border-gray-700/50"
    }`}>
      {/* Header - clicable para expandir/colapsar */}
      <button
        type="button"
        onClick={() => isMobileView && setIsExpanded(!isExpanded)}
        className={`w-full bg-gradient-to-r ${
          isCompleted 
            ? "from-green-500 to-green-600" 
            : "from-blue-500 to-blue-600"
        } px-5 py-4 text-left cursor-pointer hover:opacity-95 transition-opacity`}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold text-white truncate">{goal.name}</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isCompleted 
                ? "bg-white/30 text-white" 
                : "bg-white/20 text-white"
            }`}>
              {isCompleted ? "✓ Completada" : "Activa"}
            </span>
            <svg
              className={`w-5 h-5 text-white transition-transform md:hidden ${showExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {goal.targetDate && (
          <p className="text-sm text-white/80 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Meta: {formatDate(goal.targetDate)}
          </p>
        )}
      </button>

      {/* Resumen cuando está colapsado (solo en móvil) */}
      {showCollapsed && (
        <button
          type="button"
          className="w-full px-5 py-4 flex items-center justify-between gap-4 cursor-pointer border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
          onClick={() => setIsExpanded(true)}
          aria-label="Expandir para ver detalles"
        >
          <div className="flex-1 min-w-0">
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-1 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  isCompleted ? "bg-gradient-to-r from-green-500 to-green-600" : "bg-gradient-to-r from-blue-500 to-blue-600"
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
              {formatAmount(currentAmount)} de {formatAmount(goal.targetAmount)}
            </p>
          </div>
          <span className="text-base font-bold text-gray-700 dark:text-gray-300 flex-shrink-0">{progress.toFixed(0)}%</span>
        </button>
      )}

      {/* Contenido expandido (siempre en desktop) */}
      {showExpanded && (
      <div className="p-5">
        {/* Progreso */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Progreso</span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {progress.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 mb-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${
                isCompleted 
                  ? "bg-gradient-to-r from-green-500 to-green-600" 
                  : "bg-gradient-to-r from-blue-500 to-blue-600"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {formatAmount(currentAmount)} 
            <span className="text-sm font-normal text-gray-600 dark:text-gray-400"> de </span>
            {formatAmount(goal.targetAmount)}
          </p>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => onAddContribution(goal)}
            disabled={isCompleted}
            className="flex-1 px-4 py-2 bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:hover:bg-green-900/40 text-green-600 dark:text-green-400 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Agregar aporte"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Agregar aporte
          </button>
          <button
            onClick={() => setShowContributions(!showContributions)}
            className="flex-1 px-4 py-2 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
            aria-label={showContributions ? "Ocultar aportes" : "Ver aportes"}
          >
            <svg 
              className={`w-4 h-4 transition-transform ${showContributions ? "rotate-180" : ""}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {showContributions ? "Ocultar" : "Ver"} aportes ({goalContributions.length})
          </button>
        </div>

        {/* Lista de aportes (expandible) */}
        {showContributions && (
          <div className="border-t-2 border-gray-100 dark:border-gray-700 pt-4 mb-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Historial de aportes</h4>
            {goalContributions.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No hay aportes registrados aún
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {goalContributions
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((contribution) => (
                    <div
                      key={contribution.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900 dark:text-gray-100">
                            {formatAmount(contribution.amount)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(contribution.date)}
                          </span>
                        </div>
                        {contribution.note && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">{contribution.note}</p>
                        )}
                      </div>
                      <button
                        onClick={() => onDeleteContribution(contribution.id, goal.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Eliminar aporte"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Botones editar/eliminar */}
        <div className="flex gap-2 pt-4 border-t-2 border-gray-100 dark:border-gray-700">
          <button
            onClick={() => onEdit(goal)}
            className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
            aria-label="Editar meta"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Editar
          </button>
          <button
            onClick={() => onDelete(goal.id, goal.name)}
            className="flex-1 px-4 py-2 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
            aria-label="Eliminar meta"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Eliminar
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: GoalModal (Crear/Editar)
// ============================================================================
const GoalModal = ({ isOpen, onClose, onSave, editingGoal, currency, toDisplayAmount }) => {
  const [formData, setFormData] = useState({
    name: "",
    targetAmount: "",
    targetDate: "",
    status: "ACTIVE"
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (editingGoal) {
      const displayTarget = toDisplayAmount ? toDisplayAmount(editingGoal.targetAmount) : editingGoal.targetAmount;
      setFormData({
        name: editingGoal.name,
        targetAmount: displayTarget.toString(),
        targetDate: editingGoal.targetDate || "",
        status: editingGoal.status
      });
    } else {
      setFormData({
        name: "",
        targetAmount: "",
        targetDate: "",
        status: "ACTIVE"
      });
    }
    setErrors({});
  }, [editingGoal, isOpen, toDisplayAmount]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    if (!formData.targetAmount) {
      newErrors.targetAmount = "El objetivo es obligatorio";
    } else if (parseFloat(formData.targetAmount) <= 0) {
      newErrors.targetAmount = "El objetivo debe ser mayor a 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      ...formData,
      targetAmount: parseFloat(formData.targetAmount)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between rounded-t-2xl">
            <h2 className="text-2xl font-bold text-white">
              {editingGoal ? "Editar meta" : "Nueva meta de ahorro"}
            </h2>
            <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full transition-colors" aria-label="Cerrar">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nombre de la meta <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ej: Viaje, Emergencia, Auto..."
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  errors.name ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                }`}
              />
              {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Objetivo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Objetivo <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">{currency}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.targetAmount}
                  onChange={(e) => handleChange("targetAmount", e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    errors.targetAmount ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                  }`}
                />
              </div>
              {errors.targetAmount && <p className="mt-2 text-sm text-red-600">{errors.targetAmount}</p>}
            </div>

            {/* Fecha objetivo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fecha objetivo (opcional)
              </label>
              <input
                type="date"
                value={formData.targetDate}
                onChange={(e) => handleChange("targetDate", e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
              />
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="ACTIVE">Activa</option>
                  <option value="COMPLETED">Completada</option>
                </select>
                <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <button type="button" onClick={onClose} className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button type="submit" className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: ContributionModal
// ============================================================================
const ContributionModal = ({ isOpen, onClose, onSave, goal, currency }) => {
  const [formData, setFormData] = useState({
    amount: "",
    date: getTodayDate(),
    note: ""
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    setFormData({
      amount: "",
      date: getTodayDate(),
      note: ""
    });
    setErrors({});
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.amount) {
      newErrors.amount = "El monto es obligatorio";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "El monto debe ser mayor a 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
            <div>
              <h2 className="text-2xl font-bold text-white">Agregar aporte</h2>
              {goal && <p className="text-green-100 text-sm mt-1">Meta: {goal.name}</p>}
            </div>
            <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full transition-colors" aria-label="Cerrar">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Monto */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Monto <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">{currency}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    errors.amount ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                  }`}
                  autoFocus
                />
              </div>
              {errors.amount && <p className="mt-2 text-sm text-red-600">{errors.amount}</p>}
            </div>

            {/* Fecha */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange("date", e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
              />
            </div>

            {/* Nota */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nota (opcional)</label>
              <textarea
                value={formData.note}
                onChange={(e) => handleChange("note", e.target.value)}
                placeholder="Ej: Ahorro mensual, Extra..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all resize-none"
              />
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <button type="button" onClick={onClose} className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button type="submit" className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar aporte
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: SavingsGoals
// ============================================================================
const SavingsGoals = () => {
  const navigate = useNavigate();
  const { state, dispatch, getGoalCurrentAmount, formatDisplayAmount, toStorageAmount, toDisplayAmount } = useControlCash();
  const { confirm } = useConfirm();
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isContributionModalOpen, setIsContributionModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);

  const goals = state.goals;
  const contributions = state.contributions;

  // Calcular monto actual por meta
  const getCurrentAmount = (goalId) => {
    return getGoalCurrentAmount(goalId);
  };

  // Handlers para metas
  const handleOpenGoalModal = () => {
    setEditingGoal(null);
    setIsGoalModalOpen(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setIsGoalModalOpen(true);
  };

  const handleSaveGoal = (goalData) => {
    const targetToStore = toStorageAmount(goalData.targetAmount);
    const payload = { ...goalData, targetAmount: targetToStore };
    if (editingGoal) {
      dispatch({
        type: "UPDATE_GOAL",
        payload: {
          id: editingGoal.id,
          patch: payload
        }
      });
      console.log("✏️ Meta editada:", editingGoal.id);
    } else {
      dispatch({
        type: "CREATE_GOAL",
        payload
      });
      console.log("➕ Nueva meta creada");
    }
    setIsGoalModalOpen(false);
  };

  const handleDeleteGoal = async (goalId, name) => {
    const ok = await confirm({
      title: "Eliminar meta",
      message: `¿Estás seguro de eliminar la meta "${name}"?\n\nSe eliminarán también todos sus aportes.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({
        type: "DELETE_GOAL",
        payload: goalId
      });
      console.log("🗑️ Meta eliminada:", goalId);
    }
  };

  // Handlers para aportes
  const handleOpenContributionModal = (goal) => {
    setSelectedGoal(goal);
    setIsContributionModalOpen(true);
  };

  const handleSaveContribution = (contributionData) => {
    const amountToStore = toStorageAmount(contributionData.amount);
    dispatch({
      type: "ADD_CONTRIBUTION",
      payload: {
        ...contributionData,
        amount: amountToStore,
        goalId: selectedGoal.id
      }
    });
    console.log("💰 Nuevo aporte agregado");

    // Auto-completar meta si se alcanza el objetivo
    const newCurrentAmount = getCurrentAmount(selectedGoal.id) + amountToStore;
    if (newCurrentAmount >= selectedGoal.targetAmount && selectedGoal.status === "ACTIVE") {
      dispatch({
        type: "UPDATE_GOAL",
        payload: {
          id: selectedGoal.id,
          patch: { status: "COMPLETED" }
        }
      });
      console.log("🎉 Meta completada automáticamente:", selectedGoal.id);
    }

    setIsContributionModalOpen(false);
  };

  const handleDeleteContribution = async (contributionId, goalId) => {
    const ok = await confirm({
      title: "Eliminar aporte",
      message: "¿Estás seguro de eliminar este aporte?",
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({
        type: "DELETE_CONTRIBUTION",
        payload: contributionId
      });
      console.log("🗑️ Aporte eliminado:", contributionId);

      // Si la meta estaba completada, volver a activarla si ya no alcanza el objetivo
      const goal = goals.find(g => g.id === goalId);
      if (goal && goal.status === "COMPLETED") {
        const newCurrentAmount = contributions
          .filter(c => c.goalId === goalId && c.id !== contributionId)
          .reduce((sum, c) => sum + c.amount, 0);
        if (newCurrentAmount < goal.targetAmount) {
          dispatch({
            type: "UPDATE_GOAL",
            payload: {
              id: goalId,
              patch: { status: "ACTIVE" }
            }
          });
          console.log("🔄 Meta reactivada:", goalId);
        }
      }
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <PageHeader onBack={handleBack} />

        {/* Stats */}
        <SummaryStats goals={goals} contributions={contributions} formatAmount={formatDisplayAmount} currency={state.currencySymbol ?? state.currency} />

        {/* Botón principal */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mis metas</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {goals.length} {goals.length === 1 ? "meta registrada" : "metas registradas"}
            </p>
          </div>
          <button
            onClick={handleOpenGoalModal}
            className="px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all flex items-center gap-2"
            aria-label="Crear nueva meta"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Nueva meta</span>
            <span className="sm:hidden">Nueva</span>
          </button>
        </div>

        {/* Lista de metas */}
        {goals.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-12 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No hay metas aún</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Crea tu primera meta de ahorro</p>
            <button
              onClick={handleOpenGoalModal}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 transition-all inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Crear primera meta
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                currentAmount={getCurrentAmount(goal.id)}
                contributions={contributions}
                onAddContribution={handleOpenContributionModal}
                onEdit={handleEditGoal}
                onDelete={handleDeleteGoal}
                onDeleteContribution={handleDeleteContribution}
                formatAmount={formatDisplayAmount}
              />
            ))}
          </div>
        )}

        {/* Modales */}
        <GoalModal
          isOpen={isGoalModalOpen}
          onClose={() => setIsGoalModalOpen(false)}
          onSave={handleSaveGoal}
          editingGoal={editingGoal}
          currency={state.currencySymbol ?? state.currency}
          toDisplayAmount={toDisplayAmount}
        />
        <ContributionModal
          isOpen={isContributionModalOpen}
          onClose={() => setIsContributionModalOpen(false)}
          onSave={handleSaveContribution}
          goal={selectedGoal}
          currency={state.currencySymbol ?? state.currency}
        />
      </div>
    </Layout>
  );
};

export default SavingsGoals;
